import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Award, Users, UserCheck, UserX, Wallet, Phone, MapPin, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

export default function RankTeam() {
  const [data, setData] = useState(null);
  const [badge, setBadge] = useState('');
  const [ranks, setRanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const [t, b, rk] = await Promise.all([
          api.get('/network/team'),
          api.get('/network/rank-badge'),
          api.get('/network/ranks').catch(() => ({ data: { ranks: [] } })),
        ]);
        setData(t.data);
        setBadge(b.data?.badge_url || '');
        setRanks(rk.data?.ranks || []);
      } catch (err) {
        toast.error('Could not load your team');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="p-6 text-sm text-[var(--bz-muted)]" data-testid="rank-loading">Loading your team…</div>;
  if (!data) return <div className="p-6 text-sm text-[var(--bz-muted)]" data-testid="rank-empty">You are not part of the network yet.</div>;

  const members = data.team?.members || [];
  const filtered = tab === 'all' ? members : members.filter(m => (tab === 'active' ? m.active : !m.active));
  const currentRank = data.rank || 'Member';
  const currentLevel = ranks.find(r => r.slug === data.rank_slug || r.name === currentRank)?.level || 0;
  const nextRank = ranks.find(r => r.level > currentLevel);
  const growth = data.growth || [];
  const maxG = Math.max(1, ...growth.map(g => g.total || 0));

  return (
    <div className="max-w-screen-lg mx-auto px-4 sm:px-6 py-5 space-y-5" data-testid="rank-team-page">
      {/* Rank header */}
      <Card className="p-5 flex items-center gap-4" data-testid="rank-header">
        {badge ? (
          <img src={badge} alt="Rank badge" className="h-20 w-20 object-contain rounded-xl" data-testid="rank-badge-image" />
        ) : (
          <div className="h-20 w-20 grid place-items-center rounded-xl bg-[var(--bz-orange-tint)]">
            <Award className="h-9 w-9 text-[var(--bz-orange)]" />
          </div>
        )}
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-[var(--bz-muted)] font-bold">Your rank</div>
          <div className="text-3xl font-black display" data-testid="rank-name">{currentRank}</div>
          <div className="text-xs text-[var(--bz-muted)]">ID {data.personal_id}{nextRank ? ` · Next: ${nextRank.name}` : ' · Top rank reached'}</div>
          {nextRank && <div className="text-[11px] text-[#1E3A8A] mt-1" data-testid="next-rank-hint">To reach <b>{nextRank.name}</b>: {nextRank.desc}</div>}
        </div>
      </Card>

      {/* Rank ladder strip */}
      {ranks.length > 0 && (
        <Card className="p-4" data-testid="rank-ladder">
          <div className="font-bold display mb-3">Rank ladder</div>
          <div className="flex gap-3 overflow-x-auto pb-1">
            {ranks.map(r => {
              const earned = currentLevel >= r.level;
              return (
                <div key={r.slug} className={`shrink-0 w-24 text-center rounded-xl border p-2 ${earned ? 'border-[var(--bz-orange)] bg-[var(--bz-orange-tint)]' : 'border-[var(--bz-line)] opacity-70'}`} data-testid={`ladder-${r.slug}`}>
                  {r.badge_url ? <img src={r.badge_url} alt="" className={`h-12 w-12 mx-auto object-contain ${earned ? '' : 'grayscale opacity-50'}`} /> : <div className="h-12 w-12 mx-auto grid place-items-center"><Award className="h-6 w-6 text-[var(--bz-muted)]" /></div>}
                  <div className="text-[10px] font-bold mt-1 truncate">{r.name}</div>
                  {earned && <div className="text-[9px] text-green-600 font-bold">EARNED</div>}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Income + team counts */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="p-4" data-testid="stat-total-income">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[var(--bz-muted)] font-bold"><Wallet className="h-3.5 w-3.5" />Total income</div>
          <div className="text-2xl font-black display text-blue-800 mt-1">{money(data.income?.net)}</div>
          <div className="text-[10px] text-[var(--bz-muted)]">Gross {money(data.income?.gross)}</div>
        </Card>
        <Card className="p-4" data-testid="stat-team-total">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[var(--bz-muted)] font-bold"><Users className="h-3.5 w-3.5" />Team</div>
          <div className="text-2xl font-black display mt-1">{data.team?.total || 0}</div>
        </Card>
        <Card className="p-4" data-testid="stat-team-active">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-green-700 font-bold"><UserCheck className="h-3.5 w-3.5" />Active</div>
          <div className="text-2xl font-black display text-green-700 mt-1">{data.team?.active || 0}</div>
        </Card>
        <Card className="p-4" data-testid="stat-team-inactive">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-red-600 font-bold"><UserX className="h-3.5 w-3.5" />Inactive</div>
          <div className="text-2xl font-black display text-red-600 mt-1">{data.team?.inactive || 0}</div>
        </Card>
      </div>

      {/* Team growth chart */}
      <Card className="p-4" data-testid="team-growth-card">
        <div className="font-bold display mb-3 flex items-center gap-2"><TrendingUp className="h-4 w-4" />Team growth (active members / week)</div>
        {growth.length === 0 ? (
          <div className="text-sm text-[var(--bz-muted)]" data-testid="growth-empty">No weekly history yet — this builds up after each weekly cycle.</div>
        ) : (
          <div className="flex items-end gap-2 h-40" data-testid="growth-chart">
            {growth.map((g) => (
              <div key={g.week_key} className="flex-1 flex flex-col items-center justify-end gap-1" data-testid={`growth-bar-${g.week_key}`}>
                <div className="w-full rounded-t bg-green-500" style={{ height: `${Math.max(4, ((g.active || 0) / maxG) * 130)}px` }} title={`${g.active} active`} />
                <div className="text-[9px] text-[var(--bz-muted)] rotate-0 truncate w-full text-center">{(g.week_key || '').replace(/^\d{4}-/, '')}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Weekly payouts */}
      <Card className="p-4" data-testid="weekly-payouts-card">
        <div className="font-bold display mb-3">Weekly payouts</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)] text-left">
                <th className="py-1.5 pr-3">Week</th>
                <th className="py-1.5 pr-3">Gross</th>
                <th className="py-1.5 pr-3">TDS</th>
                <th className="py-1.5 pr-3">Net</th>
                <th className="py-1.5">Forfeited</th>
              </tr>
            </thead>
            <tbody data-testid="weekly-payouts-body">
              {(data.weekly || []).length === 0 && (
                <tr><td colSpan={5} className="py-3 text-[var(--bz-muted)]">No payouts yet.</td></tr>
              )}
              {(data.weekly || []).map((w) => (
                <tr key={w.week_key} className="border-t border-[var(--bz-line)]" data-testid={`weekly-row-${w.week_key}`}>
                  <td className="py-1.5 pr-3 font-semibold">{w.week_key === 'pending' ? 'Current (pending)' : w.week_key}</td>
                  <td className="py-1.5 pr-3">{money(w.gross)}</td>
                  <td className="py-1.5 pr-3 text-orange-600">{money(w.tds)}</td>
                  <td className="py-1.5 pr-3 text-green-700 font-semibold">{money(w.net)}</td>
                  <td className="py-1.5 text-red-500">{money(w.forfeited)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Team members */}
      <Card className="p-4" data-testid="team-members-card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="font-bold display">Team members ({members.length})</div>
          <div className="flex gap-1 text-xs">
            {['all', 'active', 'inactive'].map((t) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`team-filter-${t}`}
                className={`px-3 py-1 rounded-full font-semibold capitalize ${tab === t ? 'bg-[#1D4ED8] text-white' : 'bg-[var(--bz-soft)] text-[var(--bz-ink)]'}`}>
                {t}
              </button>
            ))}
          </div>
        </div>
        {filtered.length === 0 && <div className="text-sm text-[var(--bz-muted)]">No members in this list yet.</div>}
        <div className="grid sm:grid-cols-2 gap-2">
          {filtered.map((m) => (
            <div key={m.personal_id} className="rounded-xl border border-[var(--bz-line)] p-3 flex items-center justify-between gap-2" data-testid={`team-member-${m.personal_id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${m.active ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className="font-semibold truncate">{m.full_name || m.personal_id}</span>
                  {m.rank && m.rank !== 'Member' && m.rank !== 'Beginner' && <Badge className="bg-[var(--bz-orange)] text-[10px]">{m.rank}</Badge>}
                </div>
                <div className="text-[11px] text-[var(--bz-muted)] mt-0.5">
                  <span className={`font-bold ${m.active ? 'text-green-600' : 'text-red-500'}`} data-testid={`member-status-${m.personal_id}`}>{m.personal_id}</span>
                  {m.town && <span className="inline-flex items-center gap-0.5 ml-2"><MapPin className="h-3 w-3" />{m.town}</span>}
                </div>
              </div>
              {m.phone && (
                <a href={`tel:${m.phone}`} className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-[#1D4ED8] bg-[var(--bz-green-tint)] rounded-full px-2.5 py-1" data-testid={`member-call-${m.personal_id}`}>
                  <Phone className="h-3.5 w-3.5" />{m.phone}
                </a>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

